/**
 * Fractal Levels (Auto Support / Resistance)
 *
 * Automatically identifies significant support and resistance levels
 * based on Williams Fractals — recent pivot highs and pivot lows
 * that persist as key levels.
 *
 * Category: Support/Resistance | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const fractalLevelsIndicator: IndicatorDefinition = {
  id: "fractal_levels",
  name: "Fractal Levels",
  shortName: "FracLvl",
  category: "support_resistance",
  pane: "overlay",
  params: [
    { key: "lookback", label: "Lookback", type: "number", default: 2, min: 1, max: 10 },
  ],
  plots: [
    { key: "resistance", label: "Resistance", color: "#F44336", style: "line", lineWidth: 2 },
    { key: "support", label: "Support", color: "#4CAF50", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { lookback } = params;
    const resData: { time: number; value: number }[] = [];
    const supData: { time: number; value: number }[] = [];

    let lastResistance = NaN;
    let lastSupport = NaN;

    for (let i = lookback; i < candles.length - lookback; i++) {
      // Check for fractal high
      let isFractalHigh = true;
      for (let j = 1; j <= lookback; j++) {
        if (candles[i].high <= candles[i - j].high || candles[i].high <= candles[i + j].high) {
          isFractalHigh = false;
          break;
        }
      }
      if (isFractalHigh) lastResistance = candles[i].high;

      // Check for fractal low
      let isFractalLow = true;
      for (let j = 1; j <= lookback; j++) {
        if (candles[i].low >= candles[i - j].low || candles[i].low >= candles[i + j].low) {
          isFractalLow = false;
          break;
        }
      }
      if (isFractalLow) lastSupport = candles[i].low;

      // Plot current levels
      if (!isNaN(lastResistance)) {
        resData.push({ time: candles[i].time, value: lastResistance });
      }
      if (!isNaN(lastSupport)) {
        supData.push({ time: candles[i].time, value: lastSupport });
      }
    }

    // Fill remaining bars with last known levels
    for (let i = candles.length - lookback; i < candles.length; i++) {
      if (!isNaN(lastResistance)) {
        resData.push({ time: candles[i].time, value: lastResistance });
      }
      if (!isNaN(lastSupport)) {
        supData.push({ time: candles[i].time, value: lastSupport });
      }
    }

    return [
      { key: "resistance", data: resData },
      { key: "support", data: supData },
    ];
  },
};
